import { useState, useEffect, useRef } from 'react';

/**
 * Hook para capturar localização GPS do usuário em tempo real.
 * - Usa watchPosition para atualizações contínuas
 * - Retorna { latitude, longitude, accuracy, error, isAvailable, lastUpdate }
 */
export const useGeolocation = (options = {}) => {
  const {
    enableHighAccuracy = true,
    timeout = 15000,
    maximumAge = 30000,
    enabled = true,
  } = options;

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    isAvailable: 'geolocation' in navigator,
    lastUpdate: null,
    permissionState: 'unknown', // 'granted' | 'denied' | 'prompt' | 'unknown'
  });

  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (!('geolocation' in navigator)) {
      setLocation((prev) => ({
        ...prev,
        isAvailable: false,
        error: 'Geolocalização não suportada neste dispositivo',
      }));
      return;
    }

    // Verifica permissão (não bloqueia se a Permissions API não existir)
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setLocation((prev) => ({ ...prev, permissionState: result.state }));
          result.onchange = () => {
            setLocation((prev) => ({ ...prev, permissionState: result.state }));
          };
        })
        .catch(() => {});
    }

    const handleSuccess = (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      setLocation((prev) => ({
        ...prev,
        latitude,
        longitude,
        accuracy,
        error: null,
        lastUpdate: new Date().toISOString(),
        permissionState: 'granted',
      }));
    };

    const handleError = (err) => {
      let mensagem = 'Erro ao obter localização';
      if (err.code === 1) mensagem = 'Permissão de localização negada';
      else if (err.code === 2) mensagem = 'Localização indisponível';
      else if (err.code === 3) mensagem = 'Tempo limite ao obter localização';

      setLocation((prev) => ({
        ...prev,
        error: mensagem,
        permissionState: err.code === 1 ? 'denied' : prev.permissionState,
      }));
    };

    // Captura inicial rápida
    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
      enableHighAccuracy,
      timeout,
      maximumAge,
    });

    // Watch contínuo
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      { enableHighAccuracy, timeout, maximumAge }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, enableHighAccuracy, timeout, maximumAge]);

  return location;
};

export default useGeolocation;
